// L'écran Paramètres. Onglets, et rien d'autre : chaque panneau vit
// dans son fichier, pour qu'aucun ne dépasse 250 lignes.
//
// Tout est en lecture pour un deviseur — il doit pouvoir vérifier un
// taux sans appeler l'administrateur — et en écriture pour les seuls
// administrateurs. Chez AGC, les paramètres et les référentiels étaient
// modifiables par n'importe quel compte connecté.

import { useCallback, useState } from 'react';
import Tabs from '../../ui/Tabs.jsx';
import { ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useResource } from '../../lib/useResource.js';
import { useSession } from '../../lib/session.jsx';
import { api } from '../../lib/api.js';
import PrixPanel from './PrixPanel.jsx';
import MachinesPanel from './MachinesPanel.jsx';
import MatieresPanel from './MatieresPanel.jsx';
import OffresPanel from './OffresPanel.jsx';
import OperationsPanel from './OperationsPanel.jsx';
import SocietePanel from './SocietePanel.jsx';
import NumerotationPanel from './NumerotationPanel.jsx';
import TraitementsPanel from './TraitementsPanel.jsx';
import MailPanel from './MailPanel.jsx';
import ReglesPanel from './ReglesPanel.jsx';
import AssistantPanel from './AssistantPanel.jsx';
import './parametres.css';

const TABS = [
  { value: 'societe', label: 'Société' },
  { value: 'prix', label: 'Prix et marges' },
  { value: 'numerotation', label: 'Numérotation' },
  { value: 'machines', label: 'Parc machines' },
  { value: 'matieres', label: 'Matières' },
  { value: 'offres', label: 'Offres matière' },
  { value: 'operations', label: 'Opérations' },
  { value: 'traitements', label: 'Traitements' },
  { value: 'mail', label: 'Mail' },
  { value: 'regles', label: 'Règles métier' },
  { value: 'assistant', label: 'Assistant' },
];

export function ParametresPage() {
  const { isAdmin } = useSession();
  const [tab, setTab] = useState('societe');

  // Le parc machines sert à deux panneaux, les fournisseurs au
  // catalogue des traitements : on charge les deux une fois.
  const loadRefs = useCallback(
    () =>
      // Le parc, les fournisseurs et les MATIÈRES : l'onglet Offres a
      // besoin de la liste des nuances pour son menu déroulant, et la
      // recharger à part ferait une requête de plus à chaque bascule
      // d'onglet.
      Promise.all([
        api.get('/machines'),
        api.get('/catalogue/suppliers'),
        api.get('/catalogue/materials'),
      ]).then(([m, s, mat]) => ({ ...m, ...s, ...mat })),
    [],
  );
  const { data, error, loading, reload } = useResource(loadRefs);

  if (loading) return <SkeletonRows rows={5} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const machines = data.machines ?? [];
  const suppliers = data.suppliers ?? [];
  const materials = data.materials ?? [];

  return (
    <>
      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Sections des paramètres" />

      {!isAdmin ? (
        <p className="field__hint">
          Vous consultez les paramètres en lecture seule. Leur modification est réservée aux
          administrateurs : une erreur de taux change tous les prix.
        </p>
      ) : null}

      {tab === 'societe' ? <SocietePanel isAdmin={isAdmin} /> : null}
      {tab === 'prix' ? <PrixPanel isAdmin={isAdmin} /> : null}
      {tab === 'numerotation' ? <NumerotationPanel isAdmin={isAdmin} /> : null}
      {tab === 'machines' ? <MachinesPanel isAdmin={isAdmin} /> : null}
      {tab === 'matieres' ? <MatieresPanel isAdmin={isAdmin} /> : null}
      {tab === 'offres' ? <OffresPanel isAdmin={isAdmin} materials={materials} /> : null}
      {tab === 'operations' ? <OperationsPanel isAdmin={isAdmin} machines={machines} /> : null}
      {tab === 'traitements' ? <TraitementsPanel isAdmin={isAdmin} suppliers={suppliers} /> : null}
      {tab === 'mail' ? <MailPanel isAdmin={isAdmin} /> : null}
      {tab === 'regles' ? <ReglesPanel /> : null}
      {tab === 'assistant' ? <AssistantPanel isAdmin={isAdmin} /> : null}
    </>
  );
}

export default ParametresPage;
